// src/domains/dash.broadcast.ts —— 06 C-33 Bot 广告推送（s.page=broadcast，0049）原生 renderVals，经 domains/dash.ts 分派器挂载。
// 页内四视图：列表（状态 / 来源 / Bot 筛选、待审核角标）→ 编辑（Bot 多选、筛选、中英文案 + 计数、图片、按钮 ≤3、调度、预估、发给我预览）
// → 详情（活动信息、状态动作、runs 表）→ run 详情（5s 轮询进度直到结束、送达明细四页签：全部失败 / 拉黑 / 未找到 / 已跳过、导出 CSV）。设置弹窗与驳回原因复用 0321 通用表单。
// 数据经 pages/broadcast.contract（类型取 shared，传输委托 gp.api）。会话边界：每 host 一份 BcHost，reset 换代即丢弃（在途结果按同一性丢弃，
// 轮询停止，图片 object URL 释放），下一账号看不到上一账号的草稿与明细。
// 安全：文案预览只输出「文本 + 样式」分段（pages/broadcast.lib richSegments），模板按文本渲染；图片经鉴权 blob → object URL 显示。
import { ApiError } from '../../../shared/src/api/client';
import type { Dict } from '../../../shared/src/dc';
import { fallbackCsvName, saveBlob } from '../../../shared/src/download';
import { interpolate } from '../../../shared/src/errors';
import { createPoller, type Poller } from '../../../shared/src/poll';
import { fmtDateTime, todayIn, tzOffsetLabel } from '../../../shared/src/time';
import {
  bcBlob, bcCall, bcPage, type BcBotOption, type BcButton, type BcButtonKind, type BcCampaign, type BcCampaignInput, type BcDelivery, type BcEstimate, type BcFile,
  type BcFilterCard, type BcFilterLang, type BcPage, type BcRun, type BcRunStatus, type BcSchedule, type BcScheduleKind, type BcSettings, type BcStatus,
} from '../pages/broadcast.contract';
import { buttonUrlProblem, countText, DATE_RE, type FooterMode, FOOTER_EN, FOOTER_ZH, localToUtc, richSegments, segStyle, TIME_RE, utcToLocal } from '../pages/broadcast.lib';
import type { FormVals } from '../pages/types';
import { can, gate, gatedStyle, type Gate } from '../perm';
import type { DashPart } from './dash';
import { openConfirm, openForm } from './table';
import { str, strArr, type AdminCtx, type InputEvent } from './types';

const EMPTY = '—';
const LIST_SIZE = 20;
const SUB_SIZE = 20;
const POLL_MS = 5000; // run 详情进度轮询（06 C-33）
const MAX_BOTS = 10; // bc_campaigns.bot_ids 1..10
const MAX_BUTTONS = 3;
const IMG_MAX = 5 * 1024 * 1024; // 5 MiB
const IMG_TYPES: ReadonlySet<string> = new Set(['image/jpeg', 'image/png', 'image/webp']);
const TITLE_MAX = 64;
const NOTE_MAX = 255;

type View = 'list' | 'edit' | 'detail' | 'run';
// 失败明细页签（与代理端 / 后端对齐）：全部失败 status=failed；拉黑 / 未找到 再加 fail_kind；已跳过 status=skipped（均为单值参数）
export type FailTab = 'all' | 'blocked' | 'not_found' | 'skipped';
const FAIL_TABS: readonly FailTab[] = ['all', 'blocked', 'not_found', 'skipped'];
const TAB_KEY: Readonly<Record<FailTab, string>> = { all: 'bcTabAll', blocked: 'bcTabBlocked', not_found: 'bcTabNotFound', skipped: 'bcTabSkipped' };

export const BC_STATUSES: readonly BcStatus[] = ['draft', 'pending_review', 'rejected', 'scheduled', 'paused', 'done', 'cancelled'];
const TERMINAL_RUN: ReadonlySet<BcRunStatus> = new Set<BcRunStatus>(['done', 'cancelled', 'failed']);
const OPEN_STATUSES: ReadonlySet<BcStatus> = new Set<BcStatus>(['draft', 'pending_review', 'rejected', 'scheduled', 'paused']);
const EDITABLE: ReadonlySet<BcStatus> = new Set<BcStatus>(['draft', 'rejected', 'scheduled']);
const SUBMITTABLE: ReadonlySet<BcStatus> = new Set<BcStatus>(['draft', 'rejected']);

// ---- 编辑稿 ----

interface BtnDraft {
  uid: number;
  kind: BcButtonKind;
  label_zh: string;
  label_en: string;
  url: string;
}

export interface Draft {
  id: number | null;
  version: number;
  status: BcStatus | null;
  title: string;
  bot_ids: readonly number[];
  filter_card: BcFilterCard;
  filter_lang: BcFilterLang;
  text_zh: string;
  text_en: string;
  image_file_id: number | null;
  buttons: readonly BtnDraft[];
  kind: BcScheduleKind;
  run_at: string; // datetime-local（配置时区墙钟）
  time_of_day: string;
  weekdays: readonly number[];
  end_date: string;
}

// 自由文本字段（select 类字段另行收窄后写入）
type TextKey = 'title' | 'text_zh' | 'text_en' | 'run_at' | 'time_of_day' | 'end_date';

interface ListQ {
  status: string;
  owner_kind: string;
  bot_id: string;
  page: number;
}

interface Img {
  id: number;
  url: string; // object URL（reset / 替换时 revoke）
}

interface BcHost {
  view: View;
  q: ListQ;
  list: BcPage<BcCampaign> | null;
  listLoading: boolean;
  listTicket: number;
  pending: number;
  bots: readonly BcBotOption[] | null;
  draft: Draft | null;
  dirty: boolean;
  edErr: string;
  est: BcEstimate | null;
  imgBusy: boolean;
  img: Img | null;
  camp: BcCampaign | null;
  runs: BcPage<BcRun> | null;
  runsPage: number;
  run: BcRun | null;
  failTab: FailTab;
  dPage: number;
  dels: BcPage<BcDelivery> | null;
  poller: Poller | null;
  locks: Set<string>;
  uid: number;
}

const hosts = new WeakMap<object, BcHost>();

function fresh(): BcHost {
  return {
    view: 'list', q: { status: '', owner_kind: '', bot_id: '', page: 1 }, list: null, listLoading: false, listTicket: 0, pending: 0, bots: null,
    draft: null, dirty: false, edErr: '', est: null, imgBusy: false, img: null,
    camp: null, runs: null, runsPage: 1, run: null, failTab: 'all', dPage: 1, dels: null, poller: null, locks: new Set(), uid: 0,
  };
}

function hostOf(host: object): BcHost {
  let h = hosts.get(host);
  if (!h) {
    h = fresh();
    hosts.set(host, h);
  }
  return h;
}

function dispose(h: BcHost): void {
  h.poller?.stop();
  h.poller = null;
  dropImg(h);
}

function dropImg(h: BcHost): void {
  if (h.img) revoke(h.img.url);
  h.img = null;
}

function revoke(url: string): void {
  try {
    URL.revokeObjectURL(url);
  } catch (e) {
    console.warn('[gp:bc] revokeObjectURL 失败', e);
  }
}

function bump(ctx: AdminCtx): void {
  ctx.host.setState({ ds: { rev: ctx.host.state.ds.rev + 1 } });
}

// 在途结果只写回同一份 BcHost（会话换代 / 同页重进后丢弃）
function aliveOf(ctx: AdminCtx, h: BcHost): () => boolean {
  const host = ctx.host;
  return () => hosts.get(host) === h;
}

// 异步回调里的文案以实时字典为准（分派器传入的 ctx.t 为挂载时字典，语言可能已切换）
const dictOf = (ctx: AdminCtx): Dict => ctx.gp.ui.dict();
const tzOf = (ctx: AdminCtx): string => ctx.host.state.sh.timezone;
const time = (ctx: AdminCtx, iso: string | null | undefined): string => fmtDateTime(iso, { tz: tzOf(ctx) || undefined });

// ---- 错误文案（1012 reason / 4220 text_too_long 本地映射，其余走 ui.err） ----

const REASON_KEY: Readonly<Record<string, string>> = {
  no_telegram_binding: 'bcErrNoTg',
  preview_blocked: 'bcErrPreviewBlocked',
  campaign_status: 'bcErrStatus',
  stale_version: 'bcErrStale',
};

export function bcErrText(ctx: AdminCtx, e: unknown): string {
  const t = dictOf(ctx);
  if (e instanceof ApiError) {
    const r = e.data.reason ?? '';
    if (e.code === 1012 && REASON_KEY[r]) return str(t, REASON_KEY[r] ?? '');
    if (e.code === 4220 && r === 'text_too_long') return str(t, 'bcErrTooLong');
  }
  return ctx.gp.ui.err(e);
}

const isStale = (e: unknown): boolean => e instanceof ApiError && e.code === 1012 && e.data.reason === 'stale_version';

async function locked(ctx: AdminCtx, h: BcHost, key: string, fn: (alive: () => boolean) => Promise<void>): Promise<void> {
  if (h.locks.has(key)) return;
  h.locks.add(key);
  const alive = aliveOf(ctx, h);
  bump(ctx);
  try {
    await fn(alive);
  } catch (e) {
    if (alive()) ctx.gp.ui.toast(bcErrText(ctx, e));
    else console.warn('[gp:bc] 会话已结束，丢弃动作结果', key, e);
  } finally {
    h.locks.delete(key);
    if (alive()) bump(ctx);
  }
}

// ---- 加载 ----

function listQuery(q: ListQ): Record<string, string | number> {
  return { status: q.status, owner_kind: q.owner_kind, bot_id: q.bot_id, page: q.page, size: LIST_SIZE };
}

async function loadList(ctx: AdminCtx, h: BcHost): Promise<void> {
  const alive = aliveOf(ctx, h);
  const my = ++h.listTicket;
  h.listLoading = true;
  bump(ctx);
  try {
    const raw = await bcCall<BcPage<BcCampaign>>(ctx, 'GET /api/admin/broadcasts', { query: listQuery(h.q) });
    if (!alive() || my !== h.listTicket) return;
    h.list = bcPage(raw, h.q.page, LIST_SIZE);
  } catch (e) {
    if (alive() && my === h.listTicket) ctx.gp.ui.toast(bcErrText(ctx, e)); // 保留上一次成功数据
  } finally {
    if (alive() && my === h.listTicket) {
      h.listLoading = false;
      bump(ctx);
    }
  }
}

// 待审核角标：只取 total（size 取后端白名单最小档）
async function loadPending(ctx: AdminCtx, h: BcHost): Promise<void> {
  const alive = aliveOf(ctx, h);
  try {
    const raw = await bcCall<BcPage<BcCampaign>>(ctx, 'GET /api/admin/broadcasts', { query: { status: 'pending_review', page: 1, size: 10 } });
    if (!alive()) return;
    h.pending = bcPage(raw, 1, 10).total;
    bump(ctx);
  } catch (e) {
    console.warn('[gp:bc] 待审核计数失败', e); // 角标失败不打扰用户
  }
}

async function loadBots(ctx: AdminCtx, h: BcHost): Promise<void> {
  const alive = aliveOf(ctx, h);
  try {
    const bots = await bcCall<BcBotOption[]>(ctx, 'GET /api/admin/broadcast-bots');
    if (!alive()) return;
    h.bots = Array.isArray(bots) ? bots : [];
    bump(ctx);
  } catch (e) {
    if (alive()) ctx.gp.ui.toast(bcErrText(ctx, e));
  }
}

function refreshList(ctx: AdminCtx, h: BcHost): void {
  void loadList(ctx, h);
  void loadPending(ctx, h);
}

function setQ(ctx: AdminCtx, patch: Partial<ListQ>): void {
  const h = hostOf(ctx.host);
  h.q = { ...h.q, page: 1, ...patch };
  void loadList(ctx, h);
}

async function loadImg(ctx: AdminCtx, h: BcHost, id: number): Promise<void> {
  if (h.img?.id === id) return;
  const alive = aliveOf(ctx, h);
  try {
    const { blob } = await bcBlob(ctx, 'GET /api/admin/files/broadcast/{id}', { path: { id } });
    if (!alive()) return;
    const want = h.view === 'edit' ? h.draft?.image_file_id : h.camp?.image_file_id;
    if (want !== id) return;
    dropImg(h);
    h.img = { id, url: URL.createObjectURL(blob) };
    bump(ctx);
  } catch (e) {
    console.warn('[gp:bc] 图片读取失败', id, e);
  }
}

async function loadRuns(ctx: AdminCtx, h: BcHost, id: number): Promise<void> {
  const alive = aliveOf(ctx, h);
  const raw = await bcCall<BcPage<BcRun>>(ctx, 'GET /api/admin/broadcasts/{id}/runs', { path: { id }, query: { page: h.runsPage, size: SUB_SIZE } });
  if (!alive() || h.camp?.id !== id) return;
  h.runs = bcPage(raw, h.runsPage, SUB_SIZE);
  bump(ctx);
}

export async function openDetail(ctx: AdminCtx, id: number): Promise<void> {
  const h = hostOf(ctx.host);
  const alive = aliveOf(ctx, h);
  h.poller?.stop();
  h.poller = null;
  try {
    const c = await bcCall<BcCampaign>(ctx, 'GET /api/admin/broadcasts/{id}', { path: { id } });
    if (!alive()) return;
    if (h.camp?.id !== c.id) {
      h.runsPage = 1;
      h.runs = null;
    }
    h.camp = c;
    h.view = 'detail';
    bump(ctx);
    if (c.image_file_id !== null) void loadImg(ctx, h, c.image_file_id);
    await loadRuns(ctx, h, c.id);
  } catch (e) {
    if (alive()) ctx.gp.ui.toast(bcErrText(ctx, e));
  }
}

// ---- 编辑器 ----

function emptyDraft(): Draft {
  return {
    id: null, version: 0, status: null, title: '', bot_ids: [], filter_card: 'any', filter_lang: 'any', text_zh: '', text_en: '', image_file_id: null, buttons: [],
    kind: 'now', run_at: '', time_of_day: '', weekdays: [], end_date: '',
  };
}

function draftOf(h: BcHost, c: BcCampaign, tz: string): Draft {
  const s = c.schedule;
  return {
    id: c.id, version: c.version, status: c.status, title: c.title, bot_ids: [...c.bot_ids], filter_card: c.filter_card, filter_lang: c.filter_lang,
    text_zh: c.text_zh, text_en: c.text_en ?? '', image_file_id: c.image_file_id,
    buttons: (c.buttons ?? []).map((b) => ({ uid: ++h.uid, kind: b.kind, label_zh: b.label_zh, label_en: b.label_en, url: b.url ?? '' })),
    kind: s.kind, run_at: utcToLocal(s.run_at, tz), time_of_day: s.time_of_day ?? '', weekdays: [...(s.weekdays ?? [])], end_date: s.end_date ?? '',
  };
}

export function openEditor(ctx: AdminCtx, c: BcCampaign | null): void {
  const h = hostOf(ctx.host);
  h.poller?.stop();
  h.poller = null;
  h.draft = c ? draftOf(h, c, tzOf(ctx)) : emptyDraft();
  h.dirty = false;
  h.edErr = '';
  h.est = null;
  if (!c || c.image_file_id === null) dropImg(h);
  h.view = 'edit';
  bump(ctx);
  if (h.bots === null) void loadBots(ctx, h);
  if (c && c.image_file_id !== null) void loadImg(ctx, h, c.image_file_id);
}

function edit(ctx: AdminCtx, f: (d: Draft) => Draft, keepEst = true): void {
  const h = hostOf(ctx.host);
  if (!h.draft) return;
  h.draft = f(h.draft);
  h.dirty = true;
  h.edErr = '';
  if (!keepEst) h.est = null; // 受众条件变化：旧预估作废
  bump(ctx);
}

export function scheduleOf(d: Draft, tz: string): BcSchedule | null {
  if (d.kind === 'now') return { kind: 'now' };
  if (d.kind === 'once') {
    const run_at = localToUtc(d.run_at, tz);
    return run_at ? { kind: 'once', run_at } : null;
  }
  if (!TIME_RE.test(d.time_of_day)) return null;
  const end = d.end_date.trim();
  if (end && !DATE_RE.test(end)) return null;
  const base: BcSchedule = { kind: d.kind, time_of_day: d.time_of_day, ...(end ? { end_date: end } : {}) };
  if (d.kind === 'daily') return base;
  return d.weekdays.length ? { ...base, weekdays: [...d.weekdays].sort((a, b) => a - b) } : null;
}

export function inputOf(d: Draft, tz: string): BcCampaignInput | null {
  const schedule = scheduleOf(d, tz);
  if (!schedule) return null;
  const buttons: BcButton[] = d.buttons.map((b) =>
    b.kind === 'webapp' ? { kind: 'webapp', label_zh: b.label_zh.trim(), label_en: b.label_en.trim() } : { kind: 'url', label_zh: b.label_zh.trim(), label_en: b.label_en.trim(), url: b.url.trim() },
  );
  return {
    title: d.title.trim(), bot_ids: [...d.bot_ids], filter_card: d.filter_card, filter_lang: d.filter_lang, text_zh: d.text_zh, text_en: d.text_en,
    image_file_id: d.image_file_id, buttons, schedule,
  };
}

const URL_KEY: Readonly<Record<string, string>> = { invalid: 'bcErrUrl', https: 'bcErrUrlHttps', userinfo: 'bcErrUrl', tme: 'bcErrUrlTme' };

// 本地预检（命中即不发请求）；返回空串 = 通过
export function validateDraft(d: Draft, tz: string, t: Dict): string {
  const title = d.title.trim();
  if (!title || title.length > TITLE_MAX) return str(t, 'bcErrTitle');
  if (d.bot_ids.length === 0) return str(t, 'bcErrBots');
  if (d.bot_ids.length > MAX_BOTS) return interpolate(str(t, 'bcErrBotsMax'), { n: MAX_BOTS });
  if (!d.text_zh.trim()) return str(t, 'bcErrTextZh');
  const img = d.image_file_id !== null;
  const enEmpty = d.text_en.trim() === '';
  if (countText(d.text_zh, enEmpty ? 'max' : 'zh', img).over || (!enEmpty && countText(d.text_en, 'en', img).over)) return str(t, 'bcErrTooLong');
  for (const b of d.buttons) {
    if (!b.label_zh.trim()) return str(t, 'bcErrBtnLabel');
    if (b.kind === 'url') {
      const p = buttonUrlProblem(b.url);
      if (p) return str(t, URL_KEY[p] ?? 'bcErrUrl');
    }
  }
  if (!scheduleOf(d, tz)) return str(t, d.kind === 'once' ? 'bcErrRunAt' : d.kind === 'weekly' && d.weekdays.length === 0 ? 'bcErrWeekdays' : 'bcErrTime');
  return '';
}

// 保存（新建 POST / 编辑 PUT 带 version）；成功返回最新活动并同步稿的 id / version。go=true 时转详情
async function saveDraft(ctx: AdminCtx, h: BcHost, alive: () => boolean): Promise<BcCampaign | null> {
  const d = h.draft;
  if (!d) return null;
  const t = dictOf(ctx);
  const tz = tzOf(ctx);
  const bad = validateDraft(d, tz, t);
  const input = bad ? null : inputOf(d, tz);
  if (!input) {
    h.edErr = bad || str(t, 'bcErrTime');
    bump(ctx);
    return null;
  }
  try {
    const c =
      d.id === null
        ? await bcCall<BcCampaign>(ctx, 'POST /api/admin/broadcasts', { body: input })
        : await bcCall<BcCampaign>(ctx, 'PUT /api/admin/broadcasts/{id}', { path: { id: d.id }, body: { ...input, version: d.version } });
    if (!alive()) return null;
    if (h.draft === d || h.draft?.id === d.id) h.draft = { ...(h.draft ?? d), id: c.id, version: c.version, status: c.status };
    h.dirty = false;
    h.camp = c;
    return c;
  } catch (e) {
    if (!alive()) return null;
    h.edErr = bcErrText(ctx, e);
    bump(ctx);
    return null;
  }
}

function save(ctx: AdminCtx): Promise<void> {
  const h = hostOf(ctx.host);
  return locked(ctx, h, 'save', async (alive) => {
    const c = await saveDraft(ctx, h, alive);
    if (!c || !alive()) return;
    ctx.gp.ui.toast(str(dictOf(ctx), 'bcToastSaved'));
    await openDetail(ctx, c.id);
  });
}

// 发给我预览：未保存 / 有改动先保存（预览发的是服务端已存内容）
function preview(ctx: AdminCtx): Promise<void> {
  const h = hostOf(ctx.host);
  return locked(ctx, h, 'preview', async (alive) => {
    const d = h.draft;
    if (!d) return;
    let id = d.id;
    if (id === null || h.dirty) {
      const c = await saveDraft(ctx, h, alive);
      if (!c) return;
      id = c.id;
    }
    if (!alive()) return;
    await bcCall(ctx, 'POST /api/admin/broadcasts/{id}/preview', { path: { id } });
    if (alive()) ctx.gp.ui.toast(str(dictOf(ctx), 'bcToastPreview'));
  });
}

function estimate(ctx: AdminCtx): Promise<void> {
  const h = hostOf(ctx.host);
  return locked(ctx, h, 'estimate', async (alive) => {
    const d = h.draft;
    if (!d) return;
    if (d.bot_ids.length === 0) {
      h.edErr = str(dictOf(ctx), 'bcErrBots');
      return;
    }
    const est = await bcCall<BcEstimate>(ctx, 'POST /api/admin/broadcasts/estimate', { body: { bot_ids: [...d.bot_ids], filter_card: d.filter_card, filter_lang: d.filter_lang } });
    if (alive() && h.draft === d) h.est = est;
  });
}

interface FileEvent {
  target: { files?: ArrayLike<File> | null; value?: string };
}

export function pickImage(ctx: AdminCtx, e: FileEvent): Promise<void> {
  const file = e.target.files?.[0];
  e.target.value = ''; // 同一文件可再次选择
  const h = hostOf(ctx.host);
  if (!file || !h.draft) return Promise.resolve();
  const t = dictOf(ctx);
  if (!IMG_TYPES.has(file.type)) {
    h.edErr = str(t, 'bcErrImgType');
    bump(ctx);
    return Promise.resolve();
  }
  if (file.size > IMG_MAX) {
    h.edErr = str(t, 'bcErrImgSize');
    bump(ctx);
    return Promise.resolve();
  }
  return locked(ctx, h, 'upload', async (alive) => {
    h.imgBusy = true;
    try {
      const form = new FormData();
      form.append('file', file, file.name);
      const r = await bcCall<BcFile>(ctx, 'POST /api/admin/broadcast-files', { form });
      if (!alive() || !h.draft) return;
      dropImg(h);
      h.img = { id: r.id, url: URL.createObjectURL(file) };
      edit(ctx, (d) => ({ ...d, image_file_id: r.id }));
    } finally {
      h.imgBusy = false;
    }
  });
}

function removeImage(ctx: AdminCtx): void {
  const h = hostOf(ctx.host);
  dropImg(h);
  edit(ctx, (d) => ({ ...d, image_file_id: null }));
}

function toggleBot(ctx: AdminCtx, id: number): void {
  edit(ctx, (d) => ({ ...d, bot_ids: d.bot_ids.includes(id) ? d.bot_ids.filter((x) => x !== id) : [...d.bot_ids, id] }), false);
}

function addButton(ctx: AdminCtx): void {
  const h = hostOf(ctx.host);
  if (!h.draft || h.draft.buttons.length >= MAX_BUTTONS) return;
  const uid = ++h.uid;
  edit(ctx, (d) => ({ ...d, buttons: [...d.buttons, { uid, kind: 'url', label_zh: '', label_en: '', url: '' }] }));
}

function patchButton(ctx: AdminCtx, uid: number, p: Partial<Omit<BtnDraft, 'uid'>>): void {
  edit(ctx, (d) => ({ ...d, buttons: d.buttons.map((b) => (b.uid === uid ? { ...b, ...p } : b)) }));
}

function toggleWeekday(ctx: AdminCtx, n: number): void {
  edit(ctx, (d) => ({ ...d, weekdays: d.weekdays.includes(n) ? d.weekdays.filter((x) => x !== n) : [...d.weekdays, n] }));
}

function leaveEditor(ctx: AdminCtx): void {
  const h = hostOf(ctx.host);
  const id = h.draft?.id ?? null;
  h.draft = null;
  h.edErr = '';
  h.est = null;
  if (id !== null) void openDetail(ctx, id);
  else backToList(ctx);
}

function backToList(ctx: AdminCtx): void {
  const h = hostOf(ctx.host);
  dispose(h);
  h.view = 'list';
  h.camp = null;
  h.run = null;
  h.dels = null;
  bump(ctx);
  refreshList(ctx, h);
}

// ---- 详情：状态动作 ----

type Act = 'submit' | 'pause' | 'resume' | 'cancel' | 'approve';
const ACT_ROUTE = {
  submit: 'POST /api/admin/broadcasts/{id}/submit',
  pause: 'POST /api/admin/broadcasts/{id}/pause',
  resume: 'POST /api/admin/broadcasts/{id}/resume',
  cancel: 'POST /api/admin/broadcasts/{id}/cancel',
  approve: 'POST /api/admin/broadcasts/{id}/approve',
} as const;
const ACT_TOAST: Readonly<Record<Act, string>> = { submit: 'bcToastSubmitted', pause: 'bcToastPaused', resume: 'bcToastResumed', cancel: 'bcToastCancelled', approve: 'bcToastApproved' };

function doAct(ctx: AdminCtx, act: Act): Promise<void> {
  const h = hostOf(ctx.host);
  const c = h.camp;
  if (!c) return Promise.resolve();
  return locked(ctx, h, act, async (alive) => {
    try {
      const next = await bcCall<BcCampaign>(ctx, ACT_ROUTE[act], { path: { id: c.id }, body: { version: c.version } });
      if (!alive()) return;
      ctx.gp.ui.toast(str(dictOf(ctx), ACT_TOAST[act]));
      if (next && typeof next.id === 'number') h.camp = next;
    } catch (e) {
      // 版本冲突：提示后刷新到最新
      if (isStale(e) && alive()) void openDetail(ctx, c.id);
      throw e;
    }
    if (alive()) {
      await openDetail(ctx, c.id);
      void loadPending(ctx, h);
    }
  });
}

function confirmCancel(ctx: AdminCtx): void {
  const t = dictOf(ctx);
  openConfirm(ctx, { title: str(t, 'bcCancelTitle'), text: str(t, 'bcCancelText'), label: str(t, 'bcActCancel'), run: (c) => doAct(c, 'cancel') });
}

function confirmSubmit(ctx: AdminCtx): void {
  const t = dictOf(ctx);
  const now = hostOf(ctx.host).camp?.schedule.kind === 'now';
  openConfirm(ctx, { title: str(t, 'bcSubmitTitle'), text: str(t, now ? 'bcSubmitNowText' : 'bcSubmitText'), label: str(t, 'bcActSubmit'), run: (c) => doAct(c, 'submit') });
}

export function openReject(ctx: AdminCtx): void {
  const h = hostOf(ctx.host);
  const c = h.camp;
  if (!c) return;
  const t = dictOf(ctx);
  openForm(ctx, {
    title: `${str(t, 'bcActReject')} · ${c.title}`,
    tip: str(t, 'bcRejectTip'),
    fields: [{ k: 'note', label: str(t, 'bcRejectNote'), type: 'text', wide: true, placeholder: str(t, 'bcRejectPh') }],
    init: { note: '' },
    validate: (v, d) => (String(v['note'] ?? '').trim().length > NOTE_MAX ? interpolate(str(d, 'bcErrNoteMax'), { n: NOTE_MAX }) : ''),
    submit: async (cx, v) => {
      const note = String(v['note'] ?? '').trim();
      try {
        await bcCall<BcCampaign>(cx, 'POST /api/admin/broadcasts/{id}/reject', { path: { id: c.id }, body: { version: c.version, note } });
      } catch (e) {
        if (isStale(e)) void openDetail(cx, c.id);
        throw e;
      }
      cx.gp.ui.toast(str(dictOf(cx), 'bcToastRejected'));
      if (hosts.get(cx.host) === h) {
        void openDetail(cx, c.id);
        void loadPending(cx, h);
      }
    },
  });
}

// ---- run 详情 ----

export function deliveriesQuery(tab: FailTab, page: number): Record<string, string | number> {
  const f: Record<string, string> = tab === 'skipped' ? { status: 'skipped' } : tab === 'all' ? { status: 'failed' } : { status: 'failed', fail_kind: tab };
  return { ...f, page, size: SUB_SIZE };
}

async function loadDeliveries(ctx: AdminCtx, h: BcHost, rid: number, passive: boolean): Promise<void> {
  const alive = aliveOf(ctx, h);
  const tab = h.failTab;
  const page = h.dPage;
  const raw = await bcCall<BcPage<BcDelivery>>(ctx, 'GET /api/admin/broadcast-runs/{rid}/deliveries', { path: { rid }, query: deliveriesQuery(tab, page), passive });
  if (!alive() || h.run?.id !== rid || h.failTab !== tab || h.dPage !== page) return;
  h.dels = bcPage(raw, page, SUB_SIZE);
  bump(ctx);
}

async function pollRun(ctx: AdminCtx, h: BcHost, rid: number, first: () => boolean): Promise<void> {
  const alive = aliveOf(ctx, h);
  const passive = !first();
  try {
    const run = await bcCall<BcRun>(ctx, 'GET /api/admin/broadcast-runs/{rid}', { path: { rid }, passive });
    if (!alive() || h.view !== 'run') return;
    h.run = run;
    bump(ctx);
    if (TERMINAL_RUN.has(run.status)) {
      h.poller?.stop();
      h.poller = null;
    }
    await loadDeliveries(ctx, h, rid, passive);
  } catch (e) {
    if (alive() && !passive) ctx.gp.ui.toast(bcErrText(ctx, e));
    throw e;
  }
}

export function openRun(ctx: AdminCtx, rid: number): void {
  const h = hostOf(ctx.host);
  h.poller?.stop();
  h.view = 'run';
  h.run = null;
  h.dels = null;
  h.failTab = 'all';
  h.dPage = 1;
  bump(ctx);
  let firstTick = true;
  const first = (): boolean => {
    const f = firstTick;
    firstTick = false;
    return f;
  };
  h.poller = createPoller(() => pollRun(ctx, h, rid, first), POLL_MS);
  h.poller.start();
}

function pickFailTab(ctx: AdminCtx, tab: FailTab): void {
  const h = hostOf(ctx.host);
  const rid = h.run?.id;
  if (rid === undefined || h.failTab === tab) return;
  h.failTab = tab;
  h.dPage = 1;
  h.dels = null;
  bump(ctx);
  void loadDeliveries(ctx, h, rid, false).catch((e: unknown) => ctx.gp.ui.toast(bcErrText(ctx, e)));
}

function exportRun(ctx: AdminCtx): Promise<void> {
  const h = hostOf(ctx.host);
  const r = h.run;
  if (!r) return Promise.resolve();
  return locked(ctx, h, 'export', async (alive) => {
    const { blob, filename } = await bcBlob(ctx, 'GET /api/admin/broadcast-runs/{rid}/export', { path: { rid: r.id } });
    if (!alive()) return;
    saveBlob(blob, filename ?? fallbackCsvName('goodpayu', `broadcast-run-${r.id}`, todayIn(tzOf(ctx) || undefined)));
    ctx.gp.ui.toast(str(dictOf(ctx), 'toastExport'));
  });
}

// ---- 设置弹窗（BcSettings） ----

const SETTING_NUMS = ['agent_daily_per_bot', 'agent_max_targets', 'agent_max_periodic', 'per_bot_rate', 'global_rate'] as const;
type SettingNum = (typeof SETTING_NUMS)[number];
const INT_RE = /^\d{1,7}$/;

export function settingsFromVals(v: FormVals): BcSettings | null {
  const nums: Partial<Record<SettingNum, number>> = {};
  for (const k of SETTING_NUMS) {
    const raw = String(v[k] ?? '').trim();
    if (!INT_RE.test(raw)) return null;
    nums[k] = Number(raw);
  }
  const { agent_daily_per_bot, agent_max_targets, agent_max_periodic, per_bot_rate, global_rate } = nums;
  if (agent_daily_per_bot === undefined || agent_max_targets === undefined || agent_max_periodic === undefined || per_bot_rate === undefined || global_rate === undefined) return null;
  if (per_bot_rate < 1 || global_rate < 1) return null;
  return { agent_enabled: v['agent_enabled'] === true, agent_review: v['agent_review'] === true, agent_daily_per_bot, agent_max_targets, agent_max_periodic, per_bot_rate, global_rate };
}

export function openSettings(ctx: AdminCtx): Promise<void> {
  const h = hostOf(ctx.host);
  return locked(ctx, h, 'settings', async (alive) => {
    const s = await bcCall<BcSettings>(ctx, 'GET /api/admin/broadcast-settings');
    if (!alive()) return;
    const t = dictOf(ctx);
    const editable = can(ctx.host.state.sh.me, 'broadcast', 'edit');
    const num = (k: SettingNum, label: string) => ({ k, label: str(t, label), type: editable ? ('number' as const) : ('readonly' as const) });
    openForm(ctx, {
      title: str(t, 'bcSettings'),
      tip: str(t, editable ? 'bcSettingsTip' : 'bcSettingsRo'),
      fields: [
        { k: 'agent_enabled', label: str(t, 'bcSetAgentEnabled'), type: editable ? 'switch' : 'readonly' },
        { k: 'agent_review', label: str(t, 'bcSetAgentReview'), type: editable ? 'switch' : 'readonly' },
        num('agent_daily_per_bot', 'bcSetDaily'),
        num('agent_max_targets', 'bcSetMaxTargets'),
        num('agent_max_periodic', 'bcSetMaxPeriodic'),
        num('per_bot_rate', 'bcSetPerBotRate'),
        num('global_rate', 'bcSetGlobalRate'),
      ],
      init: editable
        ? {
            agent_enabled: s.agent_enabled, agent_review: s.agent_review, agent_daily_per_bot: String(s.agent_daily_per_bot), agent_max_targets: String(s.agent_max_targets),
            agent_max_periodic: String(s.agent_max_periodic), per_bot_rate: String(s.per_bot_rate), global_rate: String(s.global_rate),
          }
        : {
            agent_enabled: str(t, s.agent_enabled ? 'bcOn' : 'bcOff'), agent_review: str(t, s.agent_review ? 'bcOn' : 'bcOff'), agent_daily_per_bot: String(s.agent_daily_per_bot),
            agent_max_targets: String(s.agent_max_targets), agent_max_periodic: String(s.agent_max_periodic), per_bot_rate: String(s.per_bot_rate), global_rate: String(s.global_rate),
          },
      validate: (v, d) => (editable && !settingsFromVals(v) ? str(d, 'bcErrSettings') : ''),
      submit: async (cx, v) => {
        if (!editable) return; // 只读查看：保存即关闭
        const body = settingsFromVals(v);
        if (!body) return;
        await bcCall<BcSettings>(cx, 'PUT /api/admin/broadcast-settings', { body });
        cx.gp.ui.toast(str(dictOf(cx), 'bcToastSettings'));
      },
    });
  });
}

// ---- renderVals ----

const CHIP: Readonly<Record<string, readonly [string, string]>> = {
  green: ['#e3f6ee', '#0d9a68'], gray: ['#eef3f0', '#6b7772'], amber: ['#fff3df', '#c47a0c'], red: ['#fdecec', '#d1434b'], blue: ['#e7f3fb', '#1f6fa8'],
};
const STATUS_TONE: Readonly<Record<BcStatus, string>> = { draft: 'gray', pending_review: 'amber', rejected: 'red', scheduled: 'blue', paused: 'amber', done: 'green', cancelled: 'gray' };
const RUN_TONE: Readonly<Record<BcRunStatus, string>> = { queued: 'gray', snapshotting: 'blue', sending: 'blue', paused: 'amber', done: 'green', cancelled: 'gray', failed: 'red' };

function chipStyle(tone: string): string {
  const c = CHIP[tone] ?? CHIP['gray'] ?? ['#eef3f0', '#6b7772'];
  return `display:inline-block;padding:3px 9px;border-radius:999px;font-size:11px;font-weight:700;background:${c[0]};color:${c[1]};white-space:nowrap`;
}

const camel = (s: string): string => s.replace(/_([a-z])/g, (_m, c: string) => c.toUpperCase());
const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

// 状态 / run 状态 / 失败类型文案键：bcSt* / bcRun* / bcFk*；未知值回落原值（X-34）
export const statusLabel = (t: Dict, s: string): string => str(t, `bcSt${cap(camel(s))}`) || s;
const runLabel = (t: Dict, s: string): string => str(t, `bcRun${cap(camel(s))}`) || s;
const failLabel = (t: Dict, s: string | null): string => (s ? str(t, `bcFk${cap(camel(s))}`) || s : EMPTY);

const BTN = 'height:36px;padding:0 14px;border:1px solid #e6ebe8;border-radius:10px;background:#fff;font-weight:700;font-size:13px;cursor:pointer;white-space:nowrap';
const BTN_DARK = 'height:36px;padding:0 14px;border:0;border-radius:10px;background:#0f1a17;color:#fff;font-weight:700;font-size:13px;cursor:pointer;white-space:nowrap';
const BTN_GREEN = 'height:36px;padding:0 14px;border:0;border-radius:10px;background:#0d9a68;color:#fff;font-weight:700;font-size:13px;cursor:pointer;white-space:nowrap';
const BTN_DANGER = 'height:36px;padding:0 14px;border:1px solid #f3c2c5;border-radius:10px;background:#fff;color:#d1434b;font-weight:700;font-size:13px;cursor:pointer;white-space:nowrap';
const INPUT = 'height:40px;border:1px solid #dfe5e2;border-radius:10px;padding:0 12px;font-size:13px;outline:none;min-width:0;width:100%;box-sizing:border-box';
const SELECT = 'height:38px;border:1px solid #dfe5e2;border-radius:9px;padding:0 10px;font-size:13px;background:#fff;cursor:pointer';
const noop = (): void => undefined;

interface BtnV {
  label: string;
  run: () => void;
  style: string;
  title: string;
}

function button(label: string, run: () => void, style: string, g?: Gate): BtnV {
  return { label, title: g?.title ?? '', style: g ? gatedStyle(style, g) : style, run: g?.disabled ? noop : run };
}

function chipToggle(on: boolean): string {
  return `height:32px;padding:0 12px;border:1px solid ${on ? '#0d9a68' : '#e6ebe8'};border-radius:999px;background:${on ? '#e3f6ee' : '#fff'};color:${on ? '#0d9a68' : '#3b4844'};font-size:12.5px;font-weight:700;cursor:pointer;white-space:nowrap`;
}

function tabStyle(on: boolean): string {
  return `height:34px;padding:0 12px;border:0;border-bottom:2px solid ${on ? '#0d9a68' : 'transparent'};background:transparent;color:${on ? '#0f1a17' : '#8a958f'};font-size:13px;font-weight:700;cursor:pointer;white-space:nowrap`;
}

function pagerOf(t: Dict, p: BcPage<unknown> | null, cur: number, size: number, go: (page: number) => void) {
  const total = p?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / size));
  const st = (on: boolean): string => `height:28px;min-width:30px;padding:0 8px;border:1px solid #e6ebe8;border-radius:7px;background:#fff;font-size:12px;cursor:${on ? 'pointer' : 'not-allowed'};opacity:${on ? 1 : 0.4}`;
  return {
    text: interpolate(str(t, 'bcPager'), { p: cur, n: pages, total }),
    prev: cur > 1 ? () => go(cur - 1) : noop,
    next: cur < pages ? () => go(cur + 1) : noop,
    prevStyle: st(cur > 1),
    nextStyle: st(cur < pages),
  };
}

const sel = (t: Dict, opts: ReadonlyArray<readonly [string, string]>) => opts.map(([v, k]) => ({ v, label: str(t, k) || v }));

export function scheduleText(t: Dict, s: BcSchedule, tz: string): string {
  const wd = strArr(t, 'bcWeekdays');
  const end = s.end_date ? interpolate(str(t, 'bcUntil'), { d: s.end_date }) : '';
  if (s.kind === 'now') return str(t, 'bcKindNow');
  if (s.kind === 'once') return `${str(t, 'bcKindOnce')} ${fmtDateTime(s.run_at, { tz: tz || undefined })}`;
  if (s.kind === 'daily') return `${str(t, 'bcKindDaily')} ${s.time_of_day ?? ''}${end}`;
  const days = (s.weekdays ?? []).map((n) => wd[n - 1] ?? String(n)).join(str(t, 'bcSep'));
  return `${str(t, 'bcKindWeekly')}（${days}）${s.time_of_day ?? ''}${end}`;
}

const sourceText = (t: Dict, c: BcCampaign): string => (c.owner_kind === 'agent' ? `${str(t, 'bcOwnerAgent')} · ${c.agent_name ?? `#${c.agent_id ?? ''}`}` : str(t, 'bcOwnerAdmin'));
const botsText = (c: BcCampaign): string => (c.bots ?? []).map((b) => b.username).join('、') || c.bot_ids.map((id) => `#${id}`).join('、');

function lastRunText(t: Dict, c: BcCampaign): string {
  const r = c.last_run;
  if (!r) return EMPTY;
  return interpolate(str(t, 'bcLastRun'), { sent: r.sent, total: r.target_total, failed: r.failed, skipped: r.skipped });
}

function listVals(ctx: AdminCtx, h: BcHost) {
  const t = ctx.t;
  const me = ctx.s.sh.me;
  const editGate = gate(me, t, 'broadcast', 'edit');
  const botOpts = (h.bots ?? []).map((b) => ({ v: String(b.id), label: b.username }));
  const filter = (label: string, value: string, options: ReadonlyArray<{ v: string; label: string }>, set: (v: string) => void) => ({
    label: str(t, label), value, options, onChange: (e: InputEvent) => set(e.target.value), style: SELECT,
  });
  const rows = (h.list?.list ?? []).map((c) => ({
    title: c.title,
    id: `#${c.id}`,
    source: sourceText(t, c),
    bots: botsText(c),
    rule: scheduleText(t, c.schedule, tzOf(ctx)),
    status: statusLabel(t, c.status),
    statusStyle: chipStyle(STATUS_TONE[c.status] ?? 'gray'),
    next: time(ctx, c.next_run_at),
    last: lastRunText(t, c),
    open: () => void openDetail(ctx, c.id),
    view: (e: { stopPropagation(): void }) => {
      e.stopPropagation(); // 行内按钮不再冒泡触发整行 open（避免重复请求）
      void openDetail(ctx, c.id);
    },
  }));
  const pendingOn = h.q.status === 'pending_review';
  return {
    filters: [
      filter('bcFStatus', h.q.status, sel(t, BC_STATUSES.map((s) => [s, `bcSt${cap(camel(s))}`] as const)), (v) => setQ(ctx, { status: v })),
      filter('bcFOwner', h.q.owner_kind, sel(t, [['admin', 'bcOwnerAdmin'], ['agent', 'bcOwnerAgent']]), (v) => setQ(ctx, { owner_kind: v })),
      filter('bcFBot', h.q.bot_id, botOpts, (v) => setQ(ctx, { bot_id: v })),
    ],
    pendingBtn: {
      label: interpolate(str(t, 'bcPendingBtn'), { n: h.pending }),
      run: () => setQ(ctx, { status: pendingOn ? '' : 'pending_review' }),
      style: `height:36px;padding:0 14px;border:1px solid ${h.pending > 0 ? '#f0c98a' : '#e6ebe8'};border-radius:10px;background:${pendingOn ? '#fff3df' : '#fff'};color:${h.pending > 0 ? '#c47a0c' : '#6b7772'};font-weight:700;font-size:13px;cursor:pointer;white-space:nowrap`,
    },
    newBtn: button(`+ ${str(t, 'bcNew')}`, () => openEditor(ctx, null), BTN_DARK, editGate),
    settingsBtn: button(`⚙ ${str(t, 'bcSettings')}`, () => void openSettings(ctx), BTN),
    refresh: () => refreshList(ctx, h),
    loading: h.listLoading,
    rows,
    empty: !h.listLoading && rows.length === 0,
    pager: pagerOf(t, h.list, h.q.page, LIST_SIZE, (page) => {
      h.q = { ...h.q, page };
      void loadList(ctx, h);
    }),
  };
}

function countVals(t: Dict, src: string, footer: FooterMode, img: boolean) {
  const c = countText(src, footer, img);
  return { text: interpolate(str(t, 'bcCount'), { n: c.n, max: c.max }), style: `font-size:11.5px;font-weight:700;color:${c.over ? '#d1434b' : '#8a958f'}` };
}

function previewVals(src: string, footer: string) {
  return {
    segs: richSegments(src).map((s) => ({ text: s.text, style: segStyle(s), title: s.href ?? '' })),
    footer,
    empty: src.trim() === '',
  };
}

function botGroups(ctx: AdminCtx, d: Draft, bots: readonly BcBotOption[]) {
  const t = ctx.t;
  const chipOf = (id: number, label: string) => ({ label, style: chipToggle(d.bot_ids.includes(id)), toggle: () => toggleBot(ctx, id) });
  const groups: Array<{ label: string; bots: Array<ReturnType<typeof chipOf>> }> = [];
  const platform = bots.filter((b) => b.owner_kind === 'platform');
  if (platform.length) groups.push({ label: str(t, 'bcPlatformBots'), bots: platform.map((b) => chipOf(b.id, b.username)) });
  const byAgent = new Map<string, BcBotOption[]>();
  for (const b of bots) {
    if (b.owner_kind !== 'agent') continue;
    const k = b.agent_name ?? str(t, 'bcOwnerAgent');
    byAgent.set(k, [...(byAgent.get(k) ?? []), b]);
  }
  for (const [name, list] of [...byAgent].sort(([a], [b]) => a.localeCompare(b))) groups.push({ label: interpolate(str(t, 'bcAgentBots'), { name }), bots: list.map((b) => chipOf(b.id, b.username)) });
  // 已选但不在 active 列表（停用 / 转移）：单列一组以便移除
  const known = new Set(bots.map((b) => b.id));
  const stale = d.bot_ids.filter((id) => !known.has(id));
  if (stale.length) groups.push({ label: str(t, 'bcStaleBots'), bots: stale.map((id) => chipOf(id, `#${id}`)) });
  return groups;
}

function editVals(ctx: AdminCtx, h: BcHost, d: Draft) {
  const t = ctx.t;
  const me = ctx.s.sh.me;
  const editGate = gate(me, t, 'broadcast', 'edit');
  const tz = tzOf(ctx);
  const img = d.image_file_id !== null;
  const wd = strArr(t, 'bcWeekdays');
  const set = (k: TextKey) => (e: InputEvent) => edit(ctx, (x) => ({ ...x, [k]: e.target.value }));
  const est = h.est;
  return {
    heading: d.id === null ? str(t, 'bcNew') : interpolate(str(t, 'bcEditTitle'), { id: d.id }),
    reEditTip: d.status === 'scheduled' ? str(t, 'bcReEditTip') : '',
    title: d.title,
    onTitle: set('title'),
    botGroups: botGroups(ctx, d, h.bots ?? []),
    botsLoading: h.bots === null,
    botCount: interpolate(str(t, 'bcBotCount'), { n: d.bot_ids.length, max: MAX_BOTS }),
    filterCard: d.filter_card,
    onFilterCard: (e: InputEvent) => {
      const v = e.target.value;
      if (v === 'any' || v === 'has' || v === 'none') edit(ctx, (x) => ({ ...x, filter_card: v }), false);
    },
    cardOpts: sel(t, [['any', 'bcCardAny'], ['has', 'bcCardHas'], ['none', 'bcCardNone']]),
    filterLang: d.filter_lang,
    onFilterLang: (e: InputEvent) => {
      const v = e.target.value;
      if (v === 'any' || v === 'zh' || v === 'en') edit(ctx, (x) => ({ ...x, filter_lang: v }), false);
    },
    langOpts: sel(t, [['any', 'bcLangAny'], ['zh', 'bcLangZh'], ['en', 'bcLangEn']]),
    textZh: d.text_zh,
    onTextZh: set('text_zh'),
    textEn: d.text_en,
    onTextEn: set('text_en'),
    // 英文文案为空：中文正文同时下发中英用户，两个计数器均按较长尾注计
    countZh: countVals(t, d.text_zh, d.text_en.trim() === '' ? 'max' : 'zh', img),
    countEn: d.text_en.trim() === '' ? countVals(t, d.text_zh, 'max', img) : countVals(t, d.text_en, 'en', img),
    prevZh: previewVals(d.text_zh, FOOTER_ZH),
    prevEn: previewVals(d.text_en || d.text_zh, FOOTER_EN),
    img: { has: img, url: h.img && h.img.id === d.image_file_id ? h.img.url : '', busy: h.imgBusy, name: img ? `#${d.image_file_id ?? ''}` : '' },
    onFile: (e: FileEvent) => void pickImage(ctx, e),
    delImg: () => removeImage(ctx),
    btnRows: d.buttons.map((b, i) => ({
      n: i + 1,
      kind: b.kind,
      onKind: (e: InputEvent) => patchButton(ctx, b.uid, { kind: e.target.value === 'webapp' ? 'webapp' : 'url' }),
      labelZh: b.label_zh,
      onLabelZh: (e: InputEvent) => patchButton(ctx, b.uid, { label_zh: e.target.value }),
      labelEn: b.label_en,
      onLabelEn: (e: InputEvent) => patchButton(ctx, b.uid, { label_en: e.target.value }),
      isUrl: b.kind === 'url',
      url: b.url,
      onUrl: (e: InputEvent) => patchButton(ctx, b.uid, { url: e.target.value }),
      urlStyle: b.kind === 'url' && b.url.trim() !== '' && buttonUrlProblem(b.url) ? `${INPUT};border-color:#d1434b` : INPUT,
      del: () => edit(ctx, (x) => ({ ...x, buttons: x.buttons.filter((y) => y.uid !== b.uid) })),
    })),
    kindOpts: sel(t, [['url', 'bcBtnUrl'], ['webapp', 'bcBtnWebapp']]),
    addBtn: button(`+ ${str(t, 'bcAddBtn')}`, () => addButton(ctx), BTN, d.buttons.length >= MAX_BUTTONS ? { disabled: true, title: interpolate(str(t, 'bcBtnMax'), { n: MAX_BUTTONS }) } : undefined),
    kind: d.kind,
    onKind: (e: InputEvent) => {
      const v = e.target.value;
      if (v === 'now' || v === 'once' || v === 'daily' || v === 'weekly') edit(ctx, (x) => ({ ...x, kind: v }));
    },
    schedOpts: sel(t, [['now', 'bcKindNow'], ['once', 'bcKindOnce'], ['daily', 'bcKindDaily'], ['weekly', 'bcKindWeekly']]),
    isOnce: d.kind === 'once',
    isPeriodic: d.kind === 'daily' || d.kind === 'weekly',
    isWeekly: d.kind === 'weekly',
    runAt: d.run_at,
    onRunAt: set('run_at'),
    timeOfDay: d.time_of_day,
    onTimeOfDay: set('time_of_day'),
    endDate: d.end_date,
    onEndDate: set('end_date'),
    weekdays: [1, 2, 3, 4, 5, 6, 7].map((n) => ({ label: wd[n - 1] ?? String(n), style: chipToggle(d.weekdays.includes(n)), toggle: () => toggleWeekday(ctx, n) })),
    tzText: interpolate(str(t, 'bcTz'), { tz: tz || EMPTY, off: tz ? tzOffsetLabel(tz) : EMPTY }),
    estBtn: button(h.locks.has('estimate') ? str(t, 'bcEstimating') : str(t, 'bcEstimate'), () => void estimate(ctx), BTN),
    est: est
      ? {
          total: interpolate(str(t, 'bcEstTotal'), { n: est.total.toLocaleString('en-US') }),
          rows: est.by_bot.map((b) => ({ k: b.username, v: b.count.toLocaleString('en-US') })),
          capped: est.capped ? str(t, 'bcEstCapped') : '',
        }
      : null,
    err: h.edErr,
    saveBtn: button(h.locks.has('save') ? str(t, 'bcSaving') : str(t, 'bcSave'), () => void save(ctx), BTN_GREEN, editGate),
    previewBtn: button(h.locks.has('preview') ? str(t, 'bcPreviewing') : str(t, 'bcPreview'), () => void preview(ctx), BTN, editGate),
    previewTip: str(t, 'bcPreviewTip'),
    cancel: () => leaveEditor(ctx),
  };
}

function detailVals(ctx: AdminCtx, h: BcHost, c: BcCampaign) {
  const t = ctx.t;
  const me = ctx.s.sh.me;
  const eg = gate(me, t, 'broadcast', 'edit');
  const ag = gate(me, t, 'broadcast', 'approve');
  const tz = tzOf(ctx);
  const actions: BtnV[] = [];
  if (EDITABLE.has(c.status)) actions.push(button(str(t, 'bcActEdit'), () => openEditor(ctx, c), BTN, eg));
  if (SUBMITTABLE.has(c.status)) actions.push(button(str(t, 'bcActSubmit'), () => confirmSubmit(ctx), BTN_GREEN, eg));
  if (c.status === 'pending_review') {
    actions.push(button(str(t, 'bcActApprove'), () => void doAct(ctx, 'approve'), BTN_GREEN, ag));
    actions.push(button(str(t, 'bcActReject'), () => openReject(ctx), BTN_DANGER, ag));
  }
  if (c.status === 'scheduled') actions.push(button(str(t, 'bcActPause'), () => void doAct(ctx, 'pause'), BTN, eg));
  if (c.status === 'paused') actions.push(button(str(t, 'bcActResume'), () => void doAct(ctx, 'resume'), BTN, eg));
  if (OPEN_STATUSES.has(c.status)) actions.push(button(str(t, 'bcActCancel'), () => confirmCancel(ctx), BTN_DANGER, eg));
  const btnText = (c.buttons ?? []).map((b) => `${b.label_zh}${b.label_en ? ` / ${b.label_en}` : ''} → ${b.kind === 'webapp' ? str(t, 'bcBtnWebapp') : b.url ?? ''}`).join('\n');
  const runs = (h.runs?.list ?? []).map((r) => ({
    id: `#${r.id}`,
    planned: time(ctx, r.planned_at),
    status: runLabel(t, r.status),
    statusStyle: chipStyle(RUN_TONE[r.status] ?? 'gray'),
    target: `${r.target_total.toLocaleString('en-US')}${r.truncated ? ` ${str(t, 'bcTruncated')}` : ''}`,
    sent: r.sent.toLocaleString('en-US'),
    failed: (r.failed_blocked + r.failed_not_found + r.failed_other).toLocaleString('en-US'),
    skipped: r.skipped.toLocaleString('en-US'),
    finished: time(ctx, r.finished_at),
    open: () => openRun(ctx, r.id),
  }));
  const filterText = `${str(t, `bcCard${cap(c.filter_card)}`) || c.filter_card} · ${str(t, `bcLang${cap(c.filter_lang)}`) || c.filter_lang}`;
  return {
    title: c.title,
    id: `#${c.id}`,
    status: statusLabel(t, c.status),
    statusStyle: chipStyle(STATUS_TONE[c.status] ?? 'gray'),
    reviewNote: c.review_note ? interpolate(str(t, c.status === 'rejected' ? 'bcRejectedNote' : 'bcReviewNote'), { note: c.review_note }) : '',
    actions,
    fields: [
      { k: str(t, 'bcFSource'), v: sourceText(t, c) },
      { k: str(t, 'bcFBots'), v: botsText(c) },
      { k: str(t, 'bcFFilter'), v: filterText },
      { k: str(t, 'bcFSchedule'), v: scheduleText(t, c.schedule, tz) },
      { k: str(t, 'bcFNext'), v: time(ctx, c.next_run_at) },
      { k: str(t, 'bcFTz'), v: c.timezone || tz || EMPTY },
      { k: str(t, 'bcFCreated'), v: time(ctx, c.created_at) },
      { k: str(t, 'bcFUpdated'), v: time(ctx, c.updated_at) },
      { k: str(t, 'bcFButtons'), v: btnText || EMPTY },
    ],
    prevZh: previewVals(c.text_zh, FOOTER_ZH),
    prevEn: previewVals(c.text_en || c.text_zh, FOOTER_EN),
    img: { has: c.image_file_id !== null, url: h.img && h.img.id === c.image_file_id ? h.img.url : '' },
    runs,
    runsEmpty: runs.length === 0,
    runsPager: pagerOf(t, h.runs, h.runsPage, SUB_SIZE, (page) => {
      h.runsPage = page;
      void loadRuns(ctx, h, c.id).catch((e: unknown) => ctx.gp.ui.toast(bcErrText(ctx, e)));
    }),
    reload: () => void openDetail(ctx, c.id),
  };
}

function runVals(ctx: AdminCtx, h: BcHost) {
  const t = ctx.t;
  const me = ctx.s.sh.me;
  const r = h.run;
  const done = r ? r.sent + r.failed_blocked + r.failed_not_found + r.failed_other + r.skipped : 0;
  const pct = r && r.target_total > 0 ? Math.min(100, Math.floor((done / r.target_total) * 100)) : r && TERMINAL_RUN.has(r.status) ? 100 : 0;
  const counts: Readonly<Record<FailTab, number>> = {
    all: (r?.failed_blocked ?? 0) + (r?.failed_not_found ?? 0) + (r?.failed_other ?? 0),
    blocked: r?.failed_blocked ?? 0,
    not_found: r?.failed_not_found ?? 0,
    skipped: r?.skipped ?? 0,
  };
  const rows = (h.dels?.list ?? []).map((x) => ({
    user: x.user_no,
    bot: x.bot_username,
    status: str(t, `bcDs${cap(x.status)}`) || x.status,
    kind: failLabel(t, x.fail_kind),
    desc: x.fail_desc ?? EMPTY,
    at: time(ctx, x.sent_at),
  }));
  return {
    title: r ? interpolate(str(t, 'bcRunTitle'), { id: r.id, planned: time(ctx, r.planned_at) }) : str(t, 'bcLoading'),
    status: r ? runLabel(t, r.status) : EMPTY,
    statusStyle: chipStyle(r ? RUN_TONE[r.status] ?? 'gray' : 'gray'),
    polling: h.poller?.running ? str(t, 'bcPolling') : '',
    pct: `${pct}%`,
    barStyle: `height:8px;width:${pct}%;background:${r?.status === 'failed' ? '#d1434b' : '#0d9a68'};border-radius:999px;transition:width .3s`,
    progress: r ? interpolate(str(t, 'bcProgress'), { done: done.toLocaleString('en-US'), total: r.target_total.toLocaleString('en-US') }) : '',
    truncated: r?.truncated ? str(t, 'bcTruncatedTip') : '',
    stats: [
      { label: str(t, 'bcCTarget'), value: (r?.target_total ?? 0).toLocaleString('en-US') },
      { label: str(t, 'bcCSent'), value: (r?.sent ?? 0).toLocaleString('en-US') },
      { label: str(t, 'bcFkBlocked'), value: counts.blocked.toLocaleString('en-US') },
      { label: str(t, 'bcFkNotFound'), value: counts.not_found.toLocaleString('en-US') },
      { label: str(t, 'bcFkOther'), value: (r?.failed_other ?? 0).toLocaleString('en-US') },
      { label: str(t, 'bcCSkipped'), value: (r?.skipped ?? 0).toLocaleString('en-US') },
    ],
    times: r ? interpolate(str(t, 'bcRunTimes'), { s: time(ctx, r.started_at), f: time(ctx, r.finished_at) }) : '',
    tabs: FAIL_TABS.map((k) => ({ label: `${str(t, TAB_KEY[k])} ${counts[k].toLocaleString('en-US')}`, style: tabStyle(h.failTab === k), pick: () => pickFailTab(ctx, k) })),
    rows,
    empty: rows.length === 0,
    pager: pagerOf(t, h.dels, h.dPage, SUB_SIZE, (page) => {
      const rid = h.run?.id;
      if (rid === undefined) return;
      h.dPage = page;
      void loadDeliveries(ctx, h, rid, false).catch((e: unknown) => ctx.gp.ui.toast(bcErrText(ctx, e)));
    }),
    canExport: can(me, 'broadcast', 'export'),
    exportRun: () => void exportRun(ctx),
  };
}

function back(ctx: AdminCtx): void {
  const h = hostOf(ctx.host);
  if (h.view === 'run' && h.camp) {
    h.poller?.stop();
    h.poller = null;
    void openDetail(ctx, h.camp.id);
    return;
  }
  if (h.view === 'edit') return leaveEditor(ctx);
  backToList(ctx);
}

// 模板静态文案：admin 模板只直引原型 t.* 键（build split.test），新增文案经 bc.l 下发；键 = 去 bc 前缀后首字母小写（bcCTitle → l.cTitle）
const LABEL_KEYS: readonly string[] = ['bcBtnLabelEn', 'bcBtnLabelZh', 'bcBtnUrlPh', 'bcCAt', 'bcCBot', 'bcCBots', 'bcCDStatus', 'bcCDesc', 'bcCFailed', 'bcCFinished', 'bcCKind', 'bcCLast', 'bcCNext', 'bcCPlanned', 'bcCRule', 'bcCSent', 'bcCSkipped', 'bcCSource', 'bcCStatus', 'bcCTarget', 'bcCTitle', 'bcCUser', 'bcDelImg', 'bcEmpty', 'bcFails', 'bcImgHint', 'bcLBots', 'bcLButtons', 'bcLCard', 'bcLEndDate', 'bcLEstimate', 'bcLImage', 'bcLLang', 'bcLRunAt', 'bcLSchedule', 'bcLTextEn', 'bcLTextZh', 'bcLTime', 'bcLTitle', 'bcLWeekdays', 'bcLoading', 'bcPrevEn', 'bcPrevZh', 'bcRefresh', 'bcRuns', 'bcTagHint', 'bcView'];

function labels(t: Dict): Readonly<Record<string, string>> {
  return Object.fromEntries(LABEL_KEYS.map((k) => [k.charAt(2).toLowerCase() + k.slice(3), str(t, k)]));
}

const CLOSED = { isList: false, isEdit: false, isDetail: false, isRun: false } as const;

function vals(ctx: AdminCtx): Readonly<Record<string, unknown>> {
  if (ctx.s.page !== 'broadcast') return { bc: CLOSED };
  const h = hostOf(ctx.host);
  const view = h.view;
  return {
    bc: {
      isList: view === 'list',
      isEdit: view === 'edit' && h.draft !== null,
      isDetail: view === 'detail' && h.camp !== null,
      isRun: view === 'run',
      note: str(ctx.t, 'bcNote'),
      l: labels(ctx.t),
      back: () => back(ctx),
      backLabel: `‹ ${str(ctx.t, view === 'run' ? 'bcBackDetail' : view === 'edit' ? 'bcBackCancel' : 'bcBackList')}`,
      list: view === 'list' ? listVals(ctx, h) : null,
      ed: view === 'edit' && h.draft ? editVals(ctx, h, h.draft) : null,
      det: view === 'detail' && h.camp ? detailVals(ctx, h, h.camp) : null,
      run: view === 'run' ? runVals(ctx, h) : null,
    },
  };
}

export const DASH_BROADCAST: DashPart = {
  vals,
  // 进入 broadcast 页：换代回到列表，并发拉列表 / 待审核计数 / Bot 选项（筛选与编辑器共用）
  enter: (ctx) => {
    const prev = hosts.get(ctx.host);
    if (prev) dispose(prev);
    const h = fresh();
    hosts.set(ctx.host, h);
    refreshList(ctx, h);
    void loadBots(ctx, h);
  },
  reset: (host) => {
    const prev = hosts.get(host);
    if (prev) dispose(prev);
    hosts.set(host, fresh());
  },
};
